<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Incentive Setup"
                btnTitle="Add New"
                :showAddNew="true"
                @onClickAddNewButton="onClickAddNewButton"
            />
        </div>
        <div class="mt-2 px-2">
        <div class="row">
          <div class="col-12 col-sm-6 col-md-3 col-lg-3">
            <label for="colFormLabel" class="col-form-label">Incentive Type</label>
            <v-select
                placeholder="Select Incentive Type"
                v-model="formData.incentive_type"
                :options="incentiveType"
                :reduce="name => name.value"
                label="name"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-3 col-lg-3">
            <label for="colFormLabel" class="col-form-label">Status</label>
            <v-select
                placeholder="Select Status"
                v-model="formData.status"
                :options="status"
                label="name"
                :reduce="name => name.value"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-1 col-lg-1 mb-1 mt-2">
            <button
                style="min-width: 64px;"
                @click="onClickToGo"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            > Go
            </button>
          </div>
        </div>
      </div>

        <div class="col-12 px-2 mt-2 mb-4">
            <ListTable
                :tableItems="tableItems"
            />
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>

  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Generation Commission"
          btnTitle="Add New"
          :showAddNew="false"
      />
    </div>
    <div class="mt-2 px-2">
      <div class="row">
        <div class="col-12">
          <div class="card p-2" style="min-height: 200px">
              <div class="table-responsive">
                <table class="table table-bordered">
                  <thead>
                  <tr>
                    <th class="text-center">Level Key</th>
                    <th class="text-center">Percentage (%)</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, i) in generationTableItems" :key="i">
                    <td class="text-center text-capitalize fw-bold">
                      {{ item.level_name === 'all' ? 'All (Remaining)' : `Generation ${item.level_name}` }}
                    </td>
                    <td class="text-center" style="width: 200px">
                      <div class="input-group input-group-sm justify-content-center" style="max-width:160px; margin:auto">
                        <input
                            type="number"
                            class="form-control text-center"
                            v-model.number="item.percentage"
                            min="0"
                            max="100"
                            step="0.01"
                            :class="{ 'is-invalid': item.percentage < 0 || item.percentage > 100 }"
                        />
                        <span class="input-group-text">%</span>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                 </table>
                <p v-if="generationTableItems.length === 0" class="text-center mt-5">No data</p>
              </div>

              <!-- Submit -->
              <div class="d-flex justify-content-end mt-2 px-1">
                <button
                    class="btn btn-primary"
                    @click="onSubmitGenerationCommission()"
                    :disabled="saveButtonLoader"
                >
                  <i class="fas fa-save me-1"></i>Save
                </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import ListTable       from '@/components/molecule/company/hospital/incentive-setup/ListTable.vue'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import { computed } from 'vue';
import handleInventory from "@/services/modules/inventory";
import handleHospital from "@/services/modules/hospital";
import handleSdm from "@/services/modules/sdm/sdm";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const saveButtonLoader = ref(false)
const tableItems = ref([]);
const generationTableItems = ref([]);
const status = ref([]);
const incentiveType = ref([]);
const params = ref({
    offset: 30,
});
let formData = ref({
  status: 'active',
  incentive_type:'',
})

const token = computed(() => localStorage.getItem('token'));

const { fetchIncentiveSetupList } = handleHospital()
const { fetchGenerationCommissionData, createGenerationCommissionData } = handleSdm()

const { fetchHome } = handleInventory()
const onClickToGo = () => {
   getIncentiveSetupList();
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const getGenerationCommissionData = async () => {
  const query = getQuery();
  loading.value = true
  await fetchGenerationCommissionData(query).then(res => {
    if (!res.status){
      return
    }
    generationTableItems.value = res.data;
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const onSubmitGenerationCommission = async () => {
  saveButtonLoader.value = true

  const payload = {
    company_id:  route.params.companyId,
    incentive_type: 'generation_commission',
    commissions:  generationTableItems.value.map(c => ({
      level_name: c.level_name,
      percentage: c.percentage,
      id: c.id
    })),
  }

  await createGenerationCommissionData(payload, getQuery())
      .then(res => {
        if (!res.status) {
          showError(res.message);
          return
        }
        showSuccess(res.message)
        getGenerationCommissionData()
      })
      .catch(() => showError('Something went wrong.'))
      .finally(() => { saveButtonLoader.value = false })
}

function onClickAddNewButton() {
  router.push({name: `incentive-setup-add`, params: route.params, query: route.query})
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getIncentiveSetupList();
}
const getIncentiveSetupList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchIncentiveSetupList(query).then(res => {
        if (!res.status){
            store.commit("resetPagination")
            return
        }

        tableItems.value = res.data.data;
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getTypes = async () => {
  let query = getQuery();
  await fetchHome(query).then(res => {
    if (!res.status){
      status.value = [];
      incentiveType.value = [];
      return
    }
    if (res.data && res.data.status) {
      status.value = Object.keys(res.data.status)
          .filter(key => ["active", "inactive"].includes(key))
          .map(key => ({
            value: key,
            name: res.data.status[key]
          }));
    }
    if (res.data && res.data.incentive_type) {
      incentiveType.value = Object.keys(res.data.incentive_type)
          .map(key => ({
            value: key,
            name: res.data.incentive_type[key]
          }));
    }

  }).catch(err => {
    console.log(err)
  })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (formData.value.incentive_type) query += '&incentive_type=' + formData.value.incentive_type;
    if (formData.value.status) query += '&status=' + formData.value.status;
    query += '&offset=' + params.value.offset
    return query;
}

onMounted(() => {
    Promise.all([
       getIncentiveSetupList(),
       getTypes(),
       getGenerationCommissionData()
    ])
})
</script>
